import type { FieldValueOnView } from 'presentation/fieldValue/fieldValueOnView';
import { fieldValueEntityNames } from 'shared/domain/fieldValue/fieldValueClients';
import { filterFieldValuesByProcess } from 'presentation/fieldValue/filterFieldValues';
import { deepEqual } from 'shared/utils/deepEqual';
import { mapRecordValues } from 'shared/utils/object';
import { createInitialData, type StoreData } from '../types';
import type { FieldValueDictionaries, FieldValuesData, FieldValuesResult } from './types';

/**
 * Every dictionary starts as loading: it loads on first use, and its users must
 * not take it for loaded and empty in the render before that.
 */
export function createInitialFieldValuesData(): FieldValuesData {
  return Object.fromEntries(
    fieldValueEntityNames.map((entity) => [entity, { ...createInitialData<FieldValueOnView>(), loading: true }])
  ) as FieldValuesData;
}

export function areFieldValuesEqual(prev: FieldValueOnView[], next: FieldValueOnView[]): boolean {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;

  const prevById = new Map(prev.map((item) => [item._id, item]));
  return next.every((item) => deepEqual(prevById.get(item._id), item));
}

/** What consumers read: only values enabled in a process count as items. */
export function toFieldValuesResult(
  { items: allItems, loading }: StoreData<FieldValueOnView>
): Omit<FieldValuesResult, 'resync'> {
  const items = allItems.filter((item) => item.processes.length > 0);
  return { items, allItems, total: items.length, loading };
}

/** Every dictionary, as `items` of `toFieldValuesResult`; loading while any of them is. */
export function toFieldValueDictionaries(data: FieldValuesData): {
  dictionaries: FieldValueDictionaries;
  loading: boolean;
} {
  return {
    dictionaries: mapRecordValues(data, (dictionary) => toFieldValuesResult(dictionary).items),
    loading: Object.values<FieldValuesData[keyof FieldValuesData]>(data).some(({ loading }) => loading),
  };
}

/** The values of each dictionary enabled in the process; none without a process. */
export function toFieldValuesOfProcess(
  dictionaries: FieldValueDictionaries,
  processId: string | undefined
): FieldValueDictionaries {
  return mapRecordValues(dictionaries, (items) => filterFieldValuesByProcess(items, processId));
}
